/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { SelectProp } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import React, { memo, useContext, useEffect, useState } from 'react';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { Box, Theme } from '@mui/material';
import Icon from 'icons/Icon';

import { PlatformContext } from 'platform/PlatformProvider';
import { itemWithDefaultValues } from 'registry/Components';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0 0',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
    minWidth: 180,
    flex: 1,
  },
  label: {
    flex: 1,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  option: {
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    fontSize: '0.8rem',
    outline: 'none',
    overflow: 'hidden',
    padding: '4px 12px',
    position: 'relative',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    transition: 'color 0.2s',
    whiteSpace: 'nowrap',
    display: 'block',
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 4,
      bottom: 4,
      right: -1,
      width: 2,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  s: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 4,
    margin: 0,
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'left 0.2s cubic-bezier(0.2,0,0,1)',
  },
  tip: {
    flexShrink: 0,
    marginLeft: 8,
    minWidth: 16,
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.4,
  },
}));

function Option({
  value,
  label,
  selected,
  onSelect,
  disabled = false,
}: {
  value: string;
  label: string;
  selected?: boolean;
  onSelect: (val: string) => void;
  disabled?: boolean;
}) {
  const classes = useStyles();
  const onClick = () => onSelect(value);
  return (
    <div
      onKeyDown={(e) => clickOnEnterOrSpace(e, onClick)}
      role="button"
      tabIndex={0}
      onClick={onClick}
      className={clsx(
        classes.option,
        selected ? classes.selected : null,
        disabled ? classes.disabled : null,
      )}
    >
      {label}
    </div>
  );
}

function SingleSelectProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  isViewOnlyUser = false,
}: PropertyEditorProps<SelectProp, string>) {
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const [options, setOptions] = useState<
    Record<string | number, string | number | boolean>
  >(typeof def.options === 'function' ? {} : def.options);
  const [state, setState] = useState([]);
  useEffect(() => {
    if (def.optionsRefreshCallback) {
      def.optionsRefreshCallback(() => {
        setState([]);
      });
    }
  }, [def]);

  const platform = useContext(PlatformContext);

  useEffect(() => {
    if (typeof def.options === 'function') {
      const page = platform.getPage(appUid, pageId);
      const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
      def
        .options(itemWithDefaults, {
          appUid,
          pageId,
          itemId: activeItemId,
          mgr,
          platform,
        })
        .then((r) => {
          setOptions(r);
        });
    }
  }, [activeItemId, appUid, def, mgr, pageId, platform, state]);

  const keys = Object.keys(options);
  const count = keys.length;

  if (count === 0) {
    return null;
  }

  return (
    <div data-testid={property} className={classes.root}>
      <div className={classes.label}>{def.label}</div>
      <div className={classes.inner}>
        <Box
          sx={{
            width: `calc(${100 / count}% - 8px)`,
            left: `calc(${Math.round(
              (100 / count) * keys.indexOf(value),
            )}% + 4px)`,
          }}
          className={classes.s}
        />
        {keys.map((key) => (
          <Option
            disabled={isViewOnlyUser}
            key={key}
            value={key}
            label={String(options[key])}
            onSelect={(v) => {
              onChange(property, v);
            }}
            selected={value === key}
          />
        ))}
      </div>
      {helpText ? (
        <Icon
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip={helpText}
          iconName="comment-exclamation"
        />
      ) : null}
    </div>
  );
}

export default memo(SingleSelectProperty);
